import React, { useState, useMemo, FC } from 'react'

import { css } from '@emotion/core'
import { useTheme } from 'emotion-theming'
import { Formik, Form } from 'formik'
import Select from 'react-select'
import Relazsizes from 'relazsizes'
import addEventGA from 'utils/addEventGA'
import api from 'utils/api'

import Label from 'components/form/atoms/Label'
import SubmitButton from 'components/form/molecules/SubmitButton'
import FormGroup from 'components/form/molecules/FormGroup'
import TextInput from 'components/form/atoms/TextInput'
import Container from 'components/layouts/atoms/Container'
import Typography from 'components/common/atoms/Typography'
import SectionContainer from 'components/layouts/atoms/SectionContainer'
import Checkbox from 'components/form/atoms/Checkbox'

interface KoinworksWidgetProps {
  schoolId?: string
}

interface Option {
  label: string
  value: number
}

interface FormValues {
  amount: number | string
  duration: number | string
  downPayment: number | string
}

const koinworksWidgetStyle = (theme: ThemeType) => css`
  .koinworks-widget {
    &__text {
      margin-bottom: 24px;
      h2 {
        line-height: 1.5;
      }
    }
    &__description {
      margin-bottom: 16px;
    }
    &__form {
      padding: 24px;
      border-radius: 8px;
      box-shadow: 0 2px 8px 0 rgba(26, 52, 79, 0.4);
      background-color: ${theme.neutral.white};
      button {
        padding: 12px;
        line-height: 16px;
        &:hover {
          box-shadow: none;
        }
      }
      button,
      label,
      input {
        font-size: 14px;
        margin-bottom: 8px;
      }
      .form-group {
        margin-bottom: 8px;
      }
    }
    &__footer {
      display: flex;
      align-items: center;
      justify-content: center;
      img {
        height: 16px;
        margin-left: 8px;
      }
    }
    &__select {
      margin-bottom: 8px;
      &.is-error {
        .select__control {
          border: solid 1px ${theme.error.red60};
          background-color: ${theme.error.red50};

          &:active,
          &:hover,
          &:focus-within {
            background-color: ${theme.neutral.white};
          }
        }
      }
      .select__control {
        border-color: ${theme.gray.gray30};
        &:hover {
          border-color: ${theme.campus.aqua30};
          box-shadow: 0 0 0 0.5px ${theme.campus.aqua30};
        }
      }
      .select__placeholder,
      .select__single-value,
      .select__option,
      .select__input {
        font-size: 14px;
        margin: 0;
      }
      .select__option {
        padding: 8px 16px;
      }
      .select__value-container {
        height: 25px;
        padding: 2px 16px;
      }
      .select__placeholder {
        color: ${theme.gray.gray50};
      }
    }
    @media screen and (min-width: 768px) {
      &__description {
        margin-bottom: 24px;
      }
      &__flex {
        display: flex;
        .koinworks-widget__flex-input {
          flex: 1 1 50%;
          &:first-of-type {
            margin-right: 24px;
          }
        }
      }
      &__text {
        margin: 0 100px 0 0;
      }
      &__form {
        min-width: 600px;
        button {
          margin-top: 24px;
        }
      }
    }
  }
`
const unformatMoney = (value: string | number) => {
  return parseInt(value.toString().replace('Rp', '').replace(/\./g, ''))
}

const doFormatMoney = (value: number | string) => {
  if (typeof value === 'string') {
    if (value === 'Rp' || value === '') {
      return ''
    }
    return unformatMoney(value)
      .toString()
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.')
      .replace(/^/, 'Rp')
  }
  return value
    .toString()
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.')
    .replace(/^/, 'Rp')
}

const validate = (values: FormValues) => {
  const minAmount = 10000000
  const errors = {} as FormValues
  if (unformatMoney(values.amount) < minAmount) {
    errors.amount = 'Minimal pinjaman Rp. 10.000.000'
  }
  if (!values.amount) {
    errors.amount = 'Biaya kuliah belum diisi'
  }
  if (values.duration === 0) {
    errors.duration = 'Tenor belum diisi'
  }
  if (!values.downPayment) {
    errors.downPayment = 'Uang Muka belum diisi'
  }
  if (values.downPayment < 10) {
    errors.downPayment = 'Uang muka minimal 10%'
  }
  if (values.downPayment > 90) {
    errors.downPayment = 'Uang muka maksimal 90%'
  }

  return errors
}

const KoinworksWidget: FC<KoinworksWidgetProps> = ({ schoolId }) => {
  const theme: ThemeType = useTheme()
  const [formState] = useState({
    amount: '',
    duration: 0,
    downPayment: 0,
  })
  const [tca, toggleTca] = useState(false)

  const gaPrefix = 'campusprofile'

  const tenorOptions = () => {
    const data: { label: string; value: number }[] = []
    const options = [6, 9, 12, 15, 18, 21, 24]
    options.forEach((val) => data.push({ label: `${val} Bulan`, value: val }))

    return data
  }

  const doFormatPercent = (value: any) => {
    if (value < 10) return 10
    if (value > 90) return 90
    return value
  }

  const handleSubmit = async (values: FormValues, setSubmitting: (isSubmitting: boolean) => void) => {
    setSubmitting(true)
    try {
      if (schoolId) {
        const { amount, duration } = values
        const data = {
          source: 'koinworks',
          // eslint-disable-next-line @typescript-eslint/camelcase
          source_detail: {
            amount: unformatMoney(amount),
            duration,
          },
        }
        const response = await api().post(`directory/${schoolId}/leads`, { json: data })
        const responseJson = await response.json()

        if (responseJson) {
          addEventGA('engagement', 'koinworks', `ga/${gaPrefix}/bottombanner-right`, data)
          window.open('https://forms.gle/L6Md7JMe5xqCexSGA', '_blank')
        }
        setSubmitting(false)
      } else {
        addEventGA('engagement', 'koinworks', `ga/${gaPrefix}/bottombanner-right`)
        window.open('https://forms.gle/L6Md7JMe5xqCexSGA', '_blank')
        setSubmitting(false)
      }
    } catch (error) {
      setSubmitting(false)
    }
  }
  return (
    <div className="koinworks-widget" css={koinworksWidgetStyle(theme)}>
      <SectionContainer backgroundColor={theme.campus.aqua30} withPaddingBottom>
        <Container>
          <div className="koinworks-widget__flex">
            <div className="koinworks-widget__text">
              <Typography variant="title" level="2" tagName="h2" fontWeight="bold" color={theme.neutral.white}>
                Yuk Raih Gelar Sarjana Bersama Koin Pintar.
              </Typography>
              <Typography
                variant="paragraph"
                level="1"
                tagName="p"
                color={theme.neutral.white}
                className="koinworks-widget__description"
              >
                Sekarang, mengenyam pendidikan di universitas favorit bukan hanya mimpi. Tak perlu khawatir soal dana,
                dengan KoinPintar, biaya pendidikan kamu akan terasa lebih ringan dan mudah!
              </Typography>
              <Typography variant="paragraph" level="1" tagName="p" color={theme.neutral.white}>
                *Perhitungan dapat berubah tergantung dari hasil analisis tim kredit KoinWorks dengan rentang bunga
                antara 0.75% - 1.25%.
              </Typography>
            </div>
            <div className="koinworks-widget__form">
              <Formik
                initialValues={formState}
                onSubmit={(values, { setSubmitting }) => {
                  handleSubmit(values, setSubmitting)
                }}
                validate={validate}
                enableReinitialize
              >
                {(props) => {
                  const { setFieldValue, values, isValid, errors } = props
                  const minGradePercent = 9 / 100
                  const maxGradePercent = 15 / 100
                  const minAmount = 10000000

                  const loanAmountVal = useMemo(() => {
                    const { amount, downPayment } = values
                    const amountVal = unformatMoney(amount)
                    if (amountVal >= minAmount && downPayment >= 10 && downPayment <= 90) {
                      return amountVal - (downPayment / 100) * amountVal
                    }
                    return 0
                  }, [values])

                  const totalVal = useMemo(() => {
                    const { amount, downPayment, duration } = values
                    const amountVal = unformatMoney(amount)
                    if (amountVal >= minAmount && downPayment >= 10 && downPayment <= 90 && duration >= 6) {
                      const downPaymentAmount = (downPayment / 100) * amountVal
                      const fund = amountVal - downPaymentAmount

                      let totalMin = fund * (minGradePercent / 12) + fund / duration
                      let totalMax = fund * (maxGradePercent / 12) + fund / duration

                      totalMin = totalMin > 0 ? totalMin : 0
                      totalMax = totalMax > 0 ? totalMax : 0
                      return {
                        totalMin: totalMin.toFixed(0),
                        totalMax: totalMax.toFixed(0),
                      }
                    }
                    return {
                      totalMin: '0',
                      totalMax: '0',
                    }
                  }, [values])
                  return (
                    <Form>
                      <FormGroup
                        id="amount-input"
                        label="Biaya Kuliah (min. Rp 10.000.000)"
                        name="amount"
                        placeholder="Rp0"
                        onChange={(e: React.FormEvent<HTMLInputElement>) => {
                          e.currentTarget.value = doFormatMoney(e.currentTarget.value)
                          setFieldValue('amount', e.currentTarget.value, false)
                        }}
                        renderInputField={(amountProps) => (
                          <TextInput
                            onFocus={() =>
                              addEventGA('engagement', 'koinworks', `ga/${gaPrefix}/bottombanner-right-top`)
                            }
                            {...amountProps}
                          />
                        )}
                      />
                      <div className="koinworks-widget__flex">
                        <div className="koinworks-widget__flex-input">
                          <FormGroup
                            id="duration-input"
                            label="Tenor"
                            name="duration"
                            placeholder="Pilih Tenor"
                            renderInputField={(durationProps) => (
                              <Select
                                onChange={(option) => {
                                  setFieldValue('duration', (option as Option).value, false)
                                  addEventGA('engagement', 'koinworks', `ga/${gaPrefix}/bottombanner-right-mid`)
                                }}
                                options={tenorOptions()}
                                className={`koinworks-widget__select ${errors.duration ? 'is-error' : ''}`}
                                classNamePrefix="select"
                                components={{ IndicatorSeparator: () => null }}
                                {...durationProps}
                              />
                            )}
                          />
                        </div>
                        <div className="koinworks-widget__flex-input">
                          <FormGroup
                            id="down-payment-input"
                            label="Uang Muka (%)"
                            name="downPayment"
                            placeholder="0 %"
                            onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                              (event.target.value = doFormatPercent(event.currentTarget.value))
                            }
                            renderInputField={(paymentProps) => <TextInput {...paymentProps} />}
                          />
                        </div>
                      </div>
                      <div className="koinworks-widget__text-input">
                        <Label id="loan-amount" name="loan-amount" labelText="Jumlah Pinjaman" />
                        <TextInput value={`${doFormatMoney(loanAmountVal)}`} disabled />
                      </div>
                      <div className="koinworks-widget__text-input">
                        <Label id="loan-amount" name="loan-amount" labelText="Bunga" />
                        <TextInput value="0.75% - 1.25%" disabled />
                      </div>
                      <div className="koinworks-widget__text-input">
                        <Label id="monthLoan" name="monthLoan" labelText="Perkiraan Cicilan Perbulan" />
                        <TextInput
                          value={`${doFormatMoney(totalVal.totalMin)} - ${doFormatMoney(totalVal.totalMax)}`}
                          disabled
                        />
                      </div>
                      <div className="koinworks-widget__tca">
                        <Checkbox id="tca2" name="tca2" checked={tca} onChange={() => toggleTca(!tca)}>
                          <Typography tagName="span" variant="body" level="2">
                            Dengan ini Anda menyetujui syarat dan ketentuan dari Quipper Campus
                          </Typography>
                        </Checkbox>
                      </div>
                      <SubmitButton disabled={!isValid || !tca}>Daftar Sekarang</SubmitButton>
                      <div className="koinworks-widget__footer">
                        <Typography tagName="span" variant="body" level="2" color={theme.gray.gray50}>
                          Powered by
                        </Typography>
                        <Relazsizes dataSrc="/next-assets/images/koinworks/logo-koinworks.png" alt="Koinworks Logo" />
                      </div>
                    </Form>
                  )
                }}
              </Formik>
            </div>
          </div>
        </Container>
      </SectionContainer>
    </div>
  )
}

export default KoinworksWidget
